import { useState } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Phone, Mail, MapPin, MessageCircle } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import DiscountBanner from '../components/DiscountBanner';
import ReviewBanner from '../components/ReviewBanner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    serviceInterest: '',
    numberOfCats: '',
    message: '',
    discountCode: ''
  });
  const [loading, setLoading] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSelectChange = (name, value) => {
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(`${API}/contact`, formData);
      
      if (response.status === 200) {
        toast.success('Thank you! We\'ll be in touch shortly to arrange your free meet & greet.');
        // Reset form
        setFormData({
          name: '',
          email: '',
          phone: '',
          serviceInterest: '',
          numberOfCats: '',
          message: '',
          discountCode: ''
        });
      }
    } catch (error) {
      // Error logged for debugging - consider using error monitoring service in production
      toast.error('Something went wrong. Please try calling or emailing us directly.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="contact-page">
      {/* Discount Banner - Top */}
      <DiscountBanner size="large" />
      
      {/* Review Banner */}
      <ReviewBanner />
      
      {/* Booking Form Section - First for instant visibility */}
      <section className="booking-form-section">
        <div className="container">
          <Card className="booking-form-card">
            <CardHeader>
              <CardTitle>Book Your Free Meet & Greet</CardTitle>
              <p className="form-subtitle">Fill in the form and we'll be in touch to arrange a convenient time</p>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="booking-form">
                <div className="form-group">
                  <Label htmlFor="name">Your Name *</Label>
                  <Input
                    id="name"
                    name="name"
                    type="text"
                    placeholder="Enter your full name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                  />
                </div>

                <div className="form-group">
                  <Label htmlFor="email">Email Address *</Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    placeholder="your.email@example.com"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                  />
                </div>

                <div className="form-group">
                  <Label htmlFor="phone">Phone Number *</Label>
                  <Input
                    id="phone"
                    name="phone"
                    type="tel"
                    placeholder="07123 456789"
                    value={formData.phone}
                    onChange={handleInputChange}
                    required
                  />
                </div>

                <div className="form-group">
                  <Label htmlFor="serviceInterest">Service Interest *</Label>
                  <Select
                    value={formData.serviceInterest}
                    onValueChange={(value) => handleSelectChange('serviceInterest', value)}
                    required
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select a service" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="once-daily">Once Daily Visit</SelectItem>
                      <SelectItem value="twice-daily">Twice Daily Visits</SelectItem>
                      <SelectItem value="vip">VIP Extended Visit</SelectItem>
                      <SelectItem value="not-sure">Not Sure Yet</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="form-group">
                  <Label htmlFor="numberOfCats">Number of Cats *</Label>
                  <Select
                    value={formData.numberOfCats}
                    onValueChange={(value) => handleSelectChange('numberOfCats', value)}
                    required
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select number" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="1">1 cat</SelectItem>
                      <SelectItem value="2">2 cats</SelectItem>
                      <SelectItem value="3">3 cats</SelectItem>
                      <SelectItem value="4">4 cats</SelectItem>
                      <SelectItem value="5+">5 or more cats</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="form-group">
                  <Label htmlFor="message">Additional Information</Label>
                  <Textarea
                    id="message"
                    name="message"
                    placeholder="Tell us about your cats, preferred dates, special requirements, or any questions you have..."
                    value={formData.message}
                    onChange={handleInputChange}
                    rows={4}
                  />
                </div>

                <div className="form-group discount-code-group">
                  <Label htmlFor="discountCode">Discount Code (Optional)</Label>
                  <Input
                    id="discountCode"
                    name="discountCode"
                    type="text"
                    placeholder="e.g. WALSALL10"
                    value={formData.discountCode}
                    onChange={handleInputChange}
                  />
                  <p className="discount-hint">Use code <strong>WALSALL10</strong> for 10% off your first booking!</p>
                </div>

                <Button type="submit" size="lg" className="submit-button" disabled={loading}>
                  {loading ? 'Sending...' : 'Send Enquiry'}
                </Button>

                <p className="form-note">* Required fields. We'll never share your information with third parties.</p>
              </form>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Contact Information Section */}
      <section className="contact-info-section">
        <div className="container">
          <div className="contact-info-grid">
            <a href="tel:07344414860" className="contact-method-card">
              <div className="contact-method-icon">
                <Phone />
              </div>
              <div className="contact-method-details">
                <h3>Call Us</h3>
                <p>07344 414860</p>
                <span className="contact-method-note">Mon-Sun, 8am-8pm</span>
              </div>
            </a>

            <a href="mailto:Enquiries@maidforcats.co.uk" className="contact-method-card">
              <div className="contact-method-icon">
                <Mail />
              </div>
              <div className="contact-method-details">
                <h3>Email Us</h3>
                <p>Enquiries@maidforcats.co.uk</p>
                <span className="contact-method-note">We reply within 24 hours</span>
              </div>
            </a>

            <a href="https://wa.me/447344414860" target="_blank" rel="noopener noreferrer" className="contact-method-card whatsapp-card">
              <div className="contact-method-icon">
                <MessageCircle />
              </div>
              <div className="contact-method-details">
                <h3>WhatsApp</h3>
                <p>Message us anytime</p>
                <span className="contact-method-note">Quick and convenient</span>
              </div>
            </a>

            <div className="contact-method-card">
              <div className="contact-method-icon">
                <MapPin />
              </div>
              <div className="contact-method-details">
                <h3>Service Area</h3>
                <p>Walsall & Surrounding Areas</p>
                <span className="contact-method-note">West Midlands</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Map Section */}
      <section className="map-section">
        <div className="container">
          <h2 className="section-title">Find Us in Walsall</h2>
          <div className="map-container">
            <iframe
              title="Maid for Cats Location"
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d38893.35512345678!2d-2.0!3d52.586!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x4870991f5f1a7c63%3A0x5c1f0b0b0b0b0b0b!2sWalsall%2C%20UK!5e0!3m2!1sen!2suk!4v1234567890123"
              width="100%"
              height="350"
              style={{ border: 0 }}
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            ></iframe>
          </div>
          <p className="map-note">Serving Walsall and surrounding areas throughout the West Midlands</p>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
